import type { Coordinates } from '@volkswagen-onehub/audi-feature-service-visitor-locator';
import type { TrackingService } from '../models/tracking-service';
import {
  trackInternalLinkEvent,
  TrackingEvents,
  trackEventWithParams,
  trackViewChangeEvent,
  trackEvent,
  trackInitialStateEvent,
} from './trackingService';
import {
  MapAppSearchResultEnum,
  MapAppSearchResultElementNameEnum,
  searchTypeEnum,
} from '../models/tracking-data';
import type { Dealer } from '../models/dealer';

export function trackInitialState(
  trackingService: TrackingService | null,
  searchGeolocation: Coordinates,
  query: string | null,
  preferredDealer: Dealer | null,
) {
  if (!(searchGeolocation.lng !== 0 && searchGeolocation.lat !== 0) || !query) {
    trackInitialStateEvent(trackingService, TrackingEvents.VIEW_CHANGE, preferredDealer, true);
  }
}

export function trackPlaceSelectionAction(
  trackingService: TrackingService | null,
  placeName: string,
  component: string,
  pos: number | undefined,
  targetURL: string,
) {
  trackInternalLinkEvent(
    trackingService,
    TrackingEvents.MAP_APP_SEARCH,
    MapAppSearchResultEnum.LOCATION_SUBMIT,
    component,
    placeName,
    placeName,
    MapAppSearchResultElementNameEnum.TEXT_LINK,
    pos,
    targetURL,
  );
}

export function trackRadiusChangeAction(
  trackingService: TrackingService | null,
  label: string,
  value: string,
  component: string,
  targetURL: string,
) {
  trackInternalLinkEvent(
    trackingService,
    TrackingEvents.MAP_APP_SEARCH,
    MapAppSearchResultEnum.CHANGE_RADIUS,
    component,
    label,
    value,
    MapAppSearchResultElementNameEnum.DROP_DOWN,
    '',
    targetURL,
  );
}

export function trackDealerSelectionAction(
  trackingService: TrackingService | null,
  dealerName: string,
  component: string,
  searchTerm: string,
  pos: number | undefined,
  targetURL: string,
) {
  trackInternalLinkEvent(
    trackingService,
    TrackingEvents.MAP_APP_SEARCH,
    MapAppSearchResultEnum.QUICK_RESULT_SUBMIT,
    component,
    dealerName,
    searchTerm,
    MapAppSearchResultElementNameEnum.TEXT_LINK,
    pos,
    targetURL,
  );
}
export function trackDealerDetailsDisplayAction(
  trackingService: TrackingService | null,
  dealer: Dealer,
) {
  trackEventWithParams(trackingService, TrackingEvents.VIEW_CHANGE, {
    dealerID: dealer.dealerId,
    dealerName: dealer.name,
    partnerServiceDetails: dealer.services,
    matPrimaryCode: dealer.matPrimaryCode,
    kvpsPartnerKey: dealer.kvpsPartnerKey,
  });
}

export function trackAddFilterAction(
  trackingService: TrackingService | null,
  label: string,
  value: string,
  component: string,
  targetURL: string,
) {
  trackInternalLinkEvent(
    trackingService,
    TrackingEvents.MAP_APP_SEARCH,
    MapAppSearchResultEnum.ADD_FILTER,
    component,
    label,
    value,
    MapAppSearchResultElementNameEnum.TEXT_LINK,
    '',
    targetURL,
  );
}
export function trackRemoveFilterAction(
  trackingService: TrackingService | null,
  label: string,
  value: string,
  component: string,
  targetURL: string,
) {
  trackInternalLinkEvent(
    trackingService,
    TrackingEvents.MAP_APP_SEARCH,
    MapAppSearchResultEnum.REMOVE_FILTER,
    component,
    label,
    value,
    MapAppSearchResultElementNameEnum.TEXT_LINK,
    '',
    targetURL,
  );
}

export function trackFilterCancelAction(
  trackingService: TrackingService | null,
  component: string,
  targetURL: string,
) {
  trackInternalLinkEvent(
    trackingService,
    TrackingEvents.MAP_APP_SEARCH,
    MapAppSearchResultEnum.DO_NOT_STORE_FILTER,
    component,
    '',
    '',
    MapAppSearchResultElementNameEnum.ICON,
    '',
    targetURL,
  );
}

export function trackMapMarkerClickAction(
  trackingService: TrackingService | null,
  dealerName: string,
  component: string,
  targetURL: string,
) {
  trackInternalLinkEvent(
    trackingService,
    TrackingEvents.MAP_APP_SEARCH,
    MapAppSearchResultEnum.MAP_PIN_CLICK,
    component,
    dealerName,
    '',
    MapAppSearchResultElementNameEnum.ICON,
    '',
    targetURL,
  );
}

export function trackMapSearchFormSubmitAction(
  trackingService: TrackingService | null,
  searchText: string,
  component: string,
  targetURL: string,
) {
  trackInternalLinkEvent(
    trackingService,
    TrackingEvents.MAP_APP_SEARCH,
    MapAppSearchResultEnum.SUBMIT,
    component,
    '',
    searchText,
    MapAppSearchResultElementNameEnum.OTHER,
    '',
    targetURL,
  );
}

export function trackResultListDealerSelectionAction(
  trackingService: TrackingService | null,
  dealerName: string,
  component: string,
  searchTerm: string,
  pos: number | undefined,
  targetURL: string,
) {
  trackInternalLinkEvent(
    trackingService,
    TrackingEvents.MAP_APP_SEARCH,
    MapAppSearchResultEnum.RESULT_SUBMIT,
    component,
    dealerName,
    searchTerm,
    MapAppSearchResultElementNameEnum.TEXT_LINK,
    pos,
    targetURL,
  );
}

export function trackSearchThisAreaButtonAction(
  trackingService: TrackingService | null,
  btnText: string,
  component: string,
  targetURL: string,
) {
  trackInternalLinkEvent(
    trackingService,
    TrackingEvents.MAP_APP_SEARCH,
    MapAppSearchResultEnum.SEARCH_THIS_AREA,
    component,
    btnText,
    '',
    MapAppSearchResultElementNameEnum.BUTTON,
    '',
    targetURL,
  );
}

export function trackSearchByTermQuery(
  trackingService: TrackingService | null,
  query: string,
  resultsLength: number,
  selectedFilters: string[],
  searchResults: Dealer[],
) {
  trackViewChangeEvent(
    trackingService,
    TrackingEvents.VIEW_CHANGE,
    query,
    resultsLength,
    searchTypeEnum.BY_TERM,
    selectedFilters,
    searchResults,
    null,
    false,
  );
}

export function trackSearchByGeolocationQuery(
  trackingService: TrackingService | null,
  query: string,
  resultsLength: number,
  selectedFilters: string[],
  searchResults: Dealer[],
) {
  trackViewChangeEvent(
    trackingService,
    TrackingEvents.VIEW_CHANGE,
    query,
    resultsLength,
    searchTypeEnum.GEOLOCATION,
    selectedFilters,
    searchResults,
    null,
    false,
  );
}

export function trackSearchByZipCodeQuery(
  trackingService: TrackingService | null,
  query: string,
  resultsLength: number,
  selectedFilters: string[],
  searchResults: Dealer[],
) {
  trackViewChangeEvent(
    trackingService,
    TrackingEvents.VIEW_CHANGE,
    query,
    resultsLength,
    searchTypeEnum.BY_ZIPCODE,
    selectedFilters,
    searchResults,
    null,
    false,
  );
}
export function trackUserLocationAction(
  trackingService: TrackingService | null,
  action: TrackingEvents,
) {
  trackEvent(trackingService, action);
}

export function trackAppReadyEvent(
  trackingService: TrackingService | null,
  componentAdditions: Record<string, unknown>,
  componentName: string,
) {
  trackEventWithParams(
    trackingService,
    TrackingEvents.APP_READY,
    componentAdditions,
    componentName,
  );
}
